import { FullPageCard, FullPageCardStep } from 'components/layout/FullPageCard/FullPageCard';
import { Page } from 'components/layout/Page/Page';
import {
  SignupAccountDetails,
  SignupAccountDetailsOnSuccess,
} from 'components/views/pages/signup/SignupAccountDetails';
import { SignupActivated } from 'components/views/pages/signup/SignupActivated';

import { SignupCreateAccount } from 'components/views/pages/signup/SignupCreateAccount';
import { AppRoutes } from 'config/appRoutes';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import React from 'react';
import { useMemo } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AppLink } from 'components/core/AppLink/AppLink';
import { httpsUrl } from 'config/url';
import { SplitPageLayout } from 'components/layout/SplitPageLayout/SplitPageLayout';
import { User, useUserQuery } from '@alluradirect/react';
import { useIsUserLoggedInRedirectSignup } from 'components/views/pages/signup/hooks/useIsUserLoggedInRedirectToManagment';
import { IS_DIRECT_VACATIONS } from 'config/envSetup';
import { dvLogo, listClientHappyImage, logoInvertV2, signUpPageFeeScreenGuestImage } from 'config/imageUrls';

export type LoginState = 'loading' | 'loggedin' | 'notloggedin';

const StyledBox = styled(Box)`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export enum SignupSteps {
  CreateAccount = 1,
  AccountDetails = 2,
  AccountActivated = 3,
}

const steps: FullPageCardStep[] = [
  {
    step: SignupSteps.CreateAccount,
    title: 'Guest Sign Up',
    hasBack: false,
  },
  {
    step: SignupSteps.AccountDetails,
    title: 'Account Details',
    hasBack: true,
  },
  {
    step: SignupSteps.AccountActivated,
    title: 'Account Activated',
    hasBack: false,
  },
];

const SignUp = () => {
  // if we're logged in we don't need to sign up
  const userQuery = useUserQuery();
  useIsUserLoggedInRedirectSignup(userQuery);

  // OK we should be here, continue...
  const theme = useTheme<any>();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const router = useRouter();

  const goBack = () => {
    router.push(
      {
        pathname: AppRoutes.signup,
        query: { step: step - 1 },
      },
      undefined,
      { shallow: true, scroll: true },
    );
  };

  React.useEffect(() => {
    if (!('step' in router.query)) {
      router.replace(
        {
          pathname: AppRoutes.signup,
          query: { step: SignupSteps.CreateAccount },
        },
        undefined,
        { shallow: true, scroll: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSignupCreateAccountSuccess = ({ ID, FIRST_NAME, LAST_NAME, EMAIL }: User) => {
    router.push(
      {
        pathname: AppRoutes.signup,
        query: {
          step: SignupSteps.AccountDetails,
          userId: ID,
          userFirstName: FIRST_NAME,
          userLastName: LAST_NAME,
          userEmail: EMAIL,
        },
      },
      undefined,
      { shallow: true, scroll: true },
    );
  };

  const onSignupAccountDetailsSuccess: SignupAccountDetailsOnSuccess = data => {
    router.push(
      {
        pathname: AppRoutes.signup,
        query: {
          step: SignupSteps.AccountActivated,
          userId: data.id,
          userFirstName: data?.firstName,
          userLastName: data?.lastName,
          userEmail: data?.email,
        },
      },
      undefined,
      { shallow: true, scroll: true },
    );
  };

  const step = Number(router.query?.step) ?? SignupSteps.CreateAccount;
  const currentStep = useMemo(() => {
    if (step > SignupSteps.AccountActivated || step < SignupSteps.CreateAccount) {
      return steps[0];
    }
    return steps[step - 1];
  }, [step]);

  const isDv = IS_DIRECT_VACATIONS();
  const companyName = isDv ? 'DirectVacations.com' : 'alluraDirect.com';
  const logo_to_display = isDv ? dvLogo : logoInvertV2;
  const feescreen = isDv ? listClientHappyImage: signUpPageFeeScreenGuestImage;
  const caption = isDv ? 'Make memories with us' : 'Property Listing Page';


  return (
    <>
      <Page
        appSEOProps={{
          title: 'Signup',
          description: 'Signup today to book vacation rentals',
        }}
        hasHeader={!mdUp}
        headerProps={{
          logoOnly: true,
        }}
      >
        <SplitPageLayout
          LeftColumnSlot={
            <>
              <AppLink href={httpsUrl()}>
                <img src={`${logo_to_display}`} width={200} />
              </AppLink>
              <StyledBox>
                <Box justifyContent="center" alignItems="center" display="flex" flexDirection="column" mt={4}>
                  <img
                    src={`${feescreen}`}
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                  <Typography color="white" fontSize={12}>
                    <i>{caption}</i>
                  </Typography>
                </Box>
                <Box sx={{ p: { sm: 0, lg: 6 } }} style={{ paddingBottom: '0px' }}>
                  <Typography variant="h4" color="white" mb={2} mt={3} sx={{ fontSize: { sm: 28, md: 34 } }}>
                    Save money by booking on {companyName}
                  </Typography>
                  <Typography color="white">
                    The same rental property may be listed on multiple websites. Save up to 50% on fees by booking with {companyName} instead of airBNB or VRBO.
                  </Typography>
                </Box>
              </StyledBox>
            </>
          }
          RightColumnSlot={
            <FullPageCard step={currentStep} goBack={goBack} hasShadow={false}>
              {step === SignupSteps.CreateAccount ? (
                <SignupCreateAccount navigateOnSuccess={onSignupCreateAccountSuccess} />
              ) : null}
              {step === SignupSteps.AccountDetails ? (
                <SignupAccountDetails onSuccess={onSignupAccountDetailsSuccess} />
              ) : null}
              {step === SignupSteps.AccountActivated ? <SignupActivated /> : null}
            </FullPageCard>
          }
        />
      </Page>
    </>
  );
};

export default SignUp;
